/**
 * ask.vlm — catalog-connected vision chat (HF / Cloudflare / CivitAI / Forge endpoints).
 */

import { getAccessToken } from "../suite/auth";
import {
  loadSavedSession,
  saveSession,
  suiteVisionChat,
  type InferenceSession,
} from "../lib/suiteInference";
import type { EngineContext, VisionEngine, AskJob, AskResult } from "./types";

export const SUITE_VLM_COST_HINT_USD = 0.02;

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error || new Error("read failed"));
    r.readAsDataURL(blob);
  });
}

async function endpointVisionChat(
  endpointUrl: string,
  prompt: string,
  imageDataUrls: string[],
  model = "vision",
  apiKey?: string,
): Promise<AskResult> {
  const base = endpointUrl.replace(/\/$/, "");
  const paths = ["/v1/chat/completions", "/chat/completions"];
  const content: unknown[] = [{ type: "text", text: prompt.trim() }];
  for (const url of imageDataUrls) {
    content.push({ type: "image_url", image_url: { url } });
  }
  const body = {
    model,
    messages: [{ role: "user", content }],
    max_tokens: 1200,
  };
  const token = apiKey || getAccessToken();
  const errors: string[] = [];
  for (const p of paths) {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${base}${p}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(180000),
      });
      if (res.status === 404) {
        errors.push(`${p}: 404`);
        continue;
      }
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        choices?: { message?: { content?: string | { text?: string }[] } }[];
        usage?: unknown;
      };
      const c = data.choices?.[0]?.message?.content;
      let text = "";
      if (typeof c === "string") text = c;
      else if (Array.isArray(c)) {
        text = c
          .map((x) => (typeof x === "object" && x && "text" in x ? String(x.text) : ""))
          .join("");
      }
      if (!text) throw new Error("Empty response");
      return { text, usage: data.usage, backend: "endpoint" };
    } catch (e) {
      errors.push(`${p}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  throw new Error(errors.join(" · ") || "Vision endpoint failed");
}

export function createVlmEngine(): VisionEngine {
  let ctxRef: EngineContext | null = null;
  let lastDetail = "idle";

  return {
    id: "vlm",
    kind: "ask",
    label: "Ask · Suite engines",
    description: "Connect a vision model from the catalog, then ask.",
    status: "ready",

    async start(ctx: EngineContext) {
      ctxRef = ctx;
      const session = loadSavedSession();
      lastDetail = session ? `session · ${session.model.name}` : "ready";
      ctx.onEvent?.({
        type: "status",
        message: session
          ? `Ask ready · ${session.model.name}`
          : "Ask ready — connect a catalog model first",
        level: "info",
      });
    },

    stop() {
      ctxRef = null;
      lastDetail = "stopped";
    },

    async runJob(job: AskJob): Promise<AskResult> {
      if (!job.prompt?.trim()) throw new Error("Enter a question or prompt");
      const blobs: Blob[] = [];
      if (job.image) blobs.push(job.image);
      if (job.images?.length) blobs.push(...job.images);
      if (!blobs.length) throw new Error("Attach at least one image (or grab a Live frame)");

      const imageDataUrls: string[] = [];
      for (const b of blobs) imageDataUrls.push(await blobToDataUrl(b));

      const session = loadSavedSession();
      const endpoint = ctxRef?.endpoint;

      if (session) {
        lastDetail = `${session.source || "catalog"} · ${session.model.name}`;
        ctxRef?.onEvent?.({
          type: "status",
          message: `${session.model.name}…`,
          level: "info",
        });
        const result = await suiteVisionChat({
          session,
          prompt: job.prompt,
          imageDataUrls,
        });
        return {
          text: result.text,
          usage: result.usage,
          backend: result.backend,
        };
      }

      if (endpoint?.endpointUrl) {
        lastDetail = `custom · ${endpoint.endpointUrl}`;
        ctxRef?.onEvent?.({
          type: "status",
          message: "Calling connected endpoint…",
          level: "info",
        });
        return endpointVisionChat(
          endpoint.endpointUrl,
          job.prompt,
          imageDataUrls,
          endpoint.model || "vision",
          endpoint.apiKey,
        );
      }

      throw new Error("Connect a model from HF, Cloudflare, CivitAI, or Forge endpoints first.");
    },

    async health() {
      return { ok: true, detail: lastDetail };
    },
  };
}

/** Chrome helper after Connect */
export function rememberSuiteSession(s: InferenceSession | null): void {
  saveSession(s);
}
